/**
 * Búsqueda económica de grúas cercanas — Firebase + Haversine + geohash (preparado).
 * Sin Google Routes, Distance Matrix ni Compute Route Matrix.
 */
import {
    MAX_NEARBY_DRIVERS,
    MAX_INITIAL_DISPATCH_DRIVERS,
    DISPATCH_WAVE_SIZE,
    NEARBY_RADIUS_STEPS_KM,
    NEARBY_RADIUS_MAX_KM,
    DRIVER_LOCATION_MAX_AGE_MS,
} from '../config/economyConfig';
import {
    calculateHaversineDistanceKm,
    sortNearbyDriversByDistance,
} from './RouteCostControl';
import { carTypesMatch } from './carTypeUtils';

const GEOHASH_BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

export const encodeGeohash = (lat, lng, precision = 7) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
    let idx = 0;
    let bit = 0;
    let even = true;
    let geohash = '';
    let latMin = -90;
    let latMax = 90;
    let lngMin = -180;
    let lngMax = 180;

    while (geohash.length < precision) {
        if (even) {
            const mid = (lngMin + lngMax) / 2;
            if (lng >= mid) {
                idx = idx * 2 + 1;
                lngMin = mid;
            } else {
                idx = idx * 2;
                lngMax = mid;
            }
        } else {
            const mid = (latMin + latMax) / 2;
            if (lat >= mid) {
                idx = idx * 2 + 1;
                latMin = mid;
            } else {
                idx = idx * 2;
                latMax = mid;
            }
        }
        even = !even;
        if (++bit === 5) {
            geohash += GEOHASH_BASE32.charAt(idx);
            bit = 0;
            idx = 0;
        }
    }
    return geohash;
};

const isLocationFreshEnough = (location, maxAgeMs = DRIVER_LOCATION_MAX_AGE_MS) => {
    if (!location) return false;
    const ts = location.updatedAt || location.at || location.timestamp;
    if (!ts) return true;
    const parsed = typeof ts === 'number' ? ts : Date.parse(ts);
    if (!Number.isFinite(parsed)) return true;
    return Date.now() - parsed <= maxAgeMs;
};

const isEligibleDriver = (driver, options = {}) => {
    const { serviceType = null, requireApproved = true } = options;
    if (!driver || !driver.location) return false;
    if (driver.location.lat == null || driver.location.lng == null) return false;
    if (driver.queue === true || driver.isOnTrip === true) return false;
    if (driver.blocked === true || driver.isBlocked === true) return false;
    if (driver.driverActiveStatus === false) return false;
    if (requireApproved && driver.approved === false) return false;
    if (!isLocationFreshEnough(driver.location, options.maxLocationAgeMs)) return false;
    if (serviceType && driver.carType && !carTypesMatch(driver.carType, serviceType)) return false;
    return true;
};

/** Pasos de radio según driverRadius del admin (máx. 150 km). */
export const buildRadiusStepsFromSettings = (settings) => {
    const adminMax = parseFloat(settings?.driverRadius);
    const maxKm = Number.isFinite(adminMax) && adminMax > 0
        ? Math.min(adminMax, 150)
        : NEARBY_RADIUS_MAX_KM;
    const base = NEARBY_RADIUS_STEPS_KM.filter((km) => km <= maxKm);
    if (base.length && base[base.length - 1] < maxKm) {
        base.push(maxKm);
    }
    return base.length ? base : [maxKm];
};

/**
 * Busca grúas cercanas con radio progresivo (3 → 5 → 10 → 20 km o según admin).
 */
export const findNearbyTowTrucks = (customerLocation, serviceType = null, options = {}) => {
    const {
        drivers = [],
        limit = MAX_NEARBY_DRIVERS,
        convertToMile = false,
        settings = null,
    } = options;
    const radiusSteps = options.radiusSteps || (settings ? buildRadiusStepsFromSettings(settings) : NEARBY_RADIUS_STEPS_KM);
    const adminMax = parseFloat(settings?.driverRadius);
    const maxRadiusKm = options.maxRadiusKm || (
        Number.isFinite(adminMax) && adminMax > 0
            ? Math.min(adminMax, 150)
            : NEARBY_RADIUS_MAX_KM
    );

    if (!customerLocation || !drivers.length) {
        return { drivers: [], radiusKm: 0, usedGeohash: false };
    }

    const eligible = drivers.filter((d) => isEligibleDriver(d, {
        serviceType,
        requireApproved: settings?.require_driver_approved !== false,
        maxLocationAgeMs: options.maxLocationAgeMs || DRIVER_LOCATION_MAX_AGE_MS,
    }));

    const steps = radiusSteps.length ? radiusSteps : [3, 5, 10, maxRadiusKm];
    let found = [];
    let usedRadius = 0;

    for (let i = 0; i < steps.length; i++) {
        const radiusKm = Math.min(steps[i], maxRadiusKm);
        usedRadius = radiusKm;
        found = sortNearbyDriversByDistance(customerLocation, eligible, {
            limit,
            radiusKm,
            convertToMile,
            carTypeName: serviceType,
        });
        if (found.length > 0) break;
    }

    return {
        drivers: found,
        radiusKm: usedRadius,
        usedGeohash: eligible.some((d) => d.location && d.location.geohash),
    };
};

/**
 * Selecciona grúas para dispatch inicial (máx. 5) y reserva el resto para oleadas.
 */
export const selectTowTrucksForDispatch = (nearbyTowTrucks, limit = MAX_INITIAL_DISPATCH_DRIVERS) => {
    const list = Array.isArray(nearbyTowTrucks) ? nearbyTowTrucks : (nearbyTowTrucks?.drivers || []);
    const waveSize = Math.min(limit, DISPATCH_WAVE_SIZE);
    const initial = list.slice(0, waveSize);
    const reserve = list.slice(waveSize);
    return {
        initial,
        reserve,
        wave: 1,
        dispatchStatus: initial.length ? 'driver_notified' : 'no_driver_online',
        totalCandidates: list.length,
    };
};

export const getNextDispatchWave = (allNearby, notifiedIds = [], waveSize = DISPATCH_WAVE_SIZE) => {
    const notified = new Set(Object.keys(notifiedIds || {}));
    const remaining = (allNearby || []).filter((d) => d.id && !notified.has(d.id));
    const next = remaining.slice(0, waveSize);
    return {
        drivers: next,
        wave: Math.ceil((notified.size + next.length) / waveSize),
        dispatchStatus: next.length ? 'driver_notified' : 'manual_assistance_required',
        hasMore: remaining.length > next.length,
    };
};

export default {
    encodeGeohash,
    findNearbyTowTrucks,
    selectTowTrucksForDispatch,
    getNextDispatchWave,
};
